import { forwardRef } from 'react';
import type { CardSize } from '@/lib/share-card-truncation';
import styles from './PrayerShareCard.module.css';

interface Props {
  /**
   * First stacked proof line — the prefix, e.g. "I prayed for you on:".
   * Ship 2a.2 split the prior single-line proof into two centered lines
   * (prefix + datetime) inside an inner `.proofLine` group so prefix
   * and datetime read as one statement with a tighter 8px inner gap,
   * while the outer `.proofGroup` keeps its 20px gap to the optional
   * cardLabel below.
   */
  prayedOnPrefix: string;
  /**
   * Second stacked proof line — the datetime, e.g.
   * "Wed, Apr 24 · 4:53 PM".
   */
  prayedOnDatetime: string;
  /**
   * Optional curated verse text (the gift). Ship 2a.3 renders this in
   * full — no caller truncation, no CSS clamp. Caller is responsible
   * for picking a `cardSize` that keeps the combined verse + prose
   * render within the canvas-safe bound (see
   * src/lib/share-card-truncation.ts `pickCardSize`).
   */
  verseText?: string;
  /** Optional verse reference, e.g. "Psalm 147:3". */
  verseReference?: string;
  /**
   * Optional already-prefixed-and-stripped card label line, e.g.
   * "Prayed for healing" or "Prayed for the harvest". B2a.3 replaces
   * the prior gardenSummary-based summaryLine — coarser, message-
   * register, recipient-safe. Caller composes from
   * `t('share.cardLabelPrefix') + ' ' + stripCardLabelDisambig(prayer.parsed.cardLabel)`
   * (see src/lib/card-label.ts). Never raw prayer text; never
   * gardenSummary.
   */
  cardLabelLine?: string;
  /**
   * Verse size class. Picks .verseLg/Md/Sm based on verse character
   * length so the verse renders at a size that fits the canvas-safe
   * bound (see src/lib/share-card-truncation.ts `pickCardSize`).
   *
   * Undefined when there's no verse on the card; the verse slot
   * doesn't render in that state so the size class is unused.
   */
  cardSize?: CardSize;
}

const VERSE_SIZE_CLASS: Record<CardSize, string> = {
  lg: styles.verseLg,
  md: styles.verseMd,
  sm: styles.verseSm,
};

/**
 * Visual template for the outbound "I prayed for you" share card.
 * Rendered off-screen and captured to PNG by html-to-image.
 *
 * B0 safe content model (CLAUDE.md rule #48, extended from printed cards
 * to the free digital card): the card NEVER carries raw prayer text NOR
 * the user's gardenSummary. It carries an optional curated verse (text +
 * reference), the immutable proof lines ("I prayed for you on:" +
 * datetime), and an optional one-line card label ("Prayed for healing")
 * picked by Grok from a fixed allowlist. With none of the optionals it
 * is still a dignified card — proof lines + footer.
 *
 * Layout (Ship 2a.2): a centered block leads with two stacked proof
 * lines (prefix + datetime) inside an inner `.proofLine` group (tight
 * 8px inner gap so the two read as one statement). When present, the
 * cardLabel ("Prayed for …") sits below them with the outer
 * `.proofGroup`'s wider 20px gap. Then an optional verse (rule + quote
 * + reference) below. Footer pinned to the bottom.
 *
 * Sizing: absolute px, not rem — the card lives outside the user's
 * text-scale preference (it's a shared artifact, not an in-app reading
 * surface). html-to-image captures at pixelRatio 2 → crisp 2160×N PNG
 * on disk (height grows past 2400 layout px when long verses pair with
 * a long card label; see export-prayer.tsx for the measurement loop).
 *
 * The footer renders `watchHim.work` with the H in brand gold — mirrors
 * the in-app `.reverent` treatment. DNS is case-insensitive so the
 * capitalized display still resolves to the live site.
 */
const PrayerShareCard = forwardRef<HTMLDivElement, Props>(function PrayerShareCard(
  {
    prayedOnPrefix,
    prayedOnDatetime,
    verseText,
    verseReference,
    cardLabelLine,
    cardSize,
  },
  ref,
) {
  // Caller derives cardSize via pickCardSize(verseText.length) and
  // passes it through PrayerShareContent. When verseText is absent
  // the size class is unused (the verse slot doesn't render).
  const sizeKey: CardSize = cardSize ?? 'lg';
  const verseSizeClass = verseText ? VERSE_SIZE_CLASS[sizeKey] : '';

  return (
    <div ref={ref} className={styles.stage} aria-hidden="true">
      <div className={styles.card}>
        <div className={styles.topRegion} />

        <div className={styles.centerBlock}>
          {/* Two stacked proof lines (prefix + datetime, tight inner
              gap via .proofLine) then optional cardLabel below the
              outer .proofGroup's gap — one connected message:
                "I prayed for you on:"
                "Wed, Apr 24 · 4:53 PM"
                "Prayed for healing"  (optional) */}
          <div className={styles.proofGroup}>
            <div className={styles.proofLine}>
              <div className={styles.prayedOnPrefix}>{prayedOnPrefix}</div>
              <div className={styles.prayedOnDatetime}>{prayedOnDatetime}</div>
            </div>
            {cardLabelLine && (
              <div className={styles.cardLabel}>{cardLabelLine}</div>
            )}
          </div>
          {verseText && (
            <>
              <div className={styles.rule} />
              <div className={`${styles.verse} ${verseSizeClass}`}>
                {`“${verseText}”`}
              </div>
              {verseReference && (
                <div className={styles.verseRef}>{verseReference}</div>
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          watch<span className={styles.footerHim}>H</span>im.work
        </div>
      </div>
    </div>
  );
});

export default PrayerShareCard;
